import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Activity, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/status`;
const COVERAGE_API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/india/data-coverage`;

function freshnessLabel(iso) {
  if (!iso) return { label: 'no data', tone: 'red' };
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { label: 'unknown', tone: 'red' };
  const hours = (now - then) / 36e5;
  if (hours < 1) return { label: `${Math.round(hours * 60)} min ago`, tone: 'green' };
  if (hours < 12) return { label: `${hours.toFixed(1)} h ago`, tone: 'green' };
  if (hours < 36) return { label: `${Math.round(hours)} h ago`, tone: 'amber' };
  const days = hours / 24;
  return { label: `${days.toFixed(1)} days ago`, tone: 'red' };
}

function Dot({ tone }) {
  const color =
    tone === 'green' ? 'bg-emerald-500'
    : tone === 'amber' ? 'bg-amber-500'
    : 'bg-rose-500';
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const [statusRes, coverageRes] = await Promise.allSettled([
        axios.get(API, { timeout: 10_000 }),
        axios.get(COVERAGE_API, { timeout: 10_000 }),
      ]);
      if (statusRes.status === 'fulfilled') {
        setData(statusRes.value.data);
        setErr(null);
      } else {
        setErr(statusRes.reason?.message || 'Could not reach status endpoint');
      }
      if (coverageRes.status === 'fulfilled') setCoverage(coverageRes.value.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 60_000);
    return () => clearInterval(t);
  }, []);

  const classes = [
    { key: 'market_snapshot', label: 'Market snapshots', expected: '~1 hour' },
    { key: 'shareholding',    label: 'Shareholding patterns', expected: 'quarterly' },
    { key: 'fundamentals',    label: 'Fundamentals', expected: '~1 day' },
    { key: 'bse_notices',     label: 'BSE notices', expected: '~4 hours' },
    { key: 'insider_trades',  label: 'Insider trades', expected: '~1 day' },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-16 space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">System status</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Live data-freshness indicators for the KubAIr platform.
            </p>
          </div>
          <button onClick={fetchStatus} className="text-xs text-emerald-500 hover:underline">
            Refresh
          </button>
        </header>

        {loading && (
          <Card><CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking services…
          </CardContent></Card>
        )}

        {err && (
          <Card className="border-rose-500/30 bg-rose-500/5">
            <CardContent className="p-6 flex items-center gap-3">
              <XCircle className="w-5 h-5 text-rose-500" />
              <div>
                <p className="text-sm font-semibold">Cannot reach API</p>
                <p className="text-xs text-muted-foreground">{err}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />
                  <p className="text-sm font-semibold">Core services</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    {data.mongo
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      : <XCircle className="w-4 h-4 text-rose-500" />}
                    <span>MongoDB</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {data.postgres
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      : <XCircle className="w-4 h-4 text-rose-500" />}
                    <span>PostgreSQL</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {data.universe && (
              <Card>
                <CardContent className="p-5 space-y-2">
                  <p className="text-sm font-semibold">Universe coverage</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Listed securities</p>
                      <p className="text-xl font-bold">{(data.universe.total || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">AI-enriched</p>
                      <p className="text-xl font-bold">{(data.universe.ai_enriched || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {coverage?.universe && (
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <p className="text-sm font-semibold">Exchange-verified fundamentals</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Verified (NSE filings)</p>
                      <p className="text-xl font-bold text-emerald-500">
                        {(coverage.universe.verified || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Scraped only</p>
                      <p className="text-xl font-bold text-amber-500">
                        {(coverage.universe.screener_only || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Not yet covered</p>
                      <p className="text-xl font-bold text-muted-foreground">
                        {(coverage.universe.no_fundamentals || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>

                  {coverage.indices?.nifty_50 && (
                    <div className="rounded-lg border border-border/50 p-3">
                      <p className="text-xs font-semibold mb-2">Nifty 50</p>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Verified P&amp;L</p>
                          <p className="font-bold">
                            {coverage.indices.nifty_50.verified}/{coverage.indices.nifty_50.members}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Balance sheet</p>
                          <p className="font-bold">
                            {coverage.indices.nifty_50.balance_sheet_fy}/{coverage.indices.nifty_50.members}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Discover ratios</p>
                          <p className="font-bold">
                            {coverage.indices.nifty_50.ratios}/{coverage.indices.nifty_50.members}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Latest filed period:{' '}
                    <span className="text-foreground font-medium">
                      {coverage.freshness?.latest_filed_period || '—'}
                    </span>
                    {coverage.verified_rows_by_status?.length > 0 && (
                      <>
                        {' · '}
                        {coverage.verified_rows_by_status
                          .map((s) => `${s.rows.toLocaleString('en-IN')} ${s.status?.toLowerCase()}`)
                          .join(' · ')}{' '}
                        rows
                      </>
                    )}
                  </p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-5 space-y-3">
                <p className="text-sm font-semibold">Data freshness</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground text-left">
                      <th className="py-1.5 font-medium">Data class</th>
                      <th className="py-1.5 font-medium">Last update</th>
                      <th className="py-1.5 font-medium">Target cadence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map(({ key, label, expected }) => {
                      const f = freshnessLabel(data.data_freshness?.[key]);
                      return (
                        <tr key={key} className="border-t border-border/40">
                          <td className="py-2.5 font-medium">{label}</td>
                          <td className="py-2.5 flex items-center gap-2">
                            <Dot tone={f.tone} /> <span className="text-muted-foreground">{f.label}</span>
                          </td>
                          <td className="py-2.5 text-xs text-muted-foreground">{expected}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground text-center">
              Probed at {new Date(data.checked_at).toLocaleString()} · environment: {data.environment}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
